import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Anchor,
  Check,
  Fish,
  SlidersHorizontal,
  User,
  UserCheck,
  Users,
  Waves,
  X,
} from "lucide-react";
import {
  CLOSE_MAP_TOOLS_PANELS_EVENT,
  MAP_TOOLS_PANEL_STATE_EVENT,
  OPEN_MAP_FILTERS_EVENT,
} from "./mapToolsEvents";
import "../../styles/components/MapControls.css";

const ALL_MARKER_FILTER = "all";
const NONE_MARKER_FILTER = "none";
const RELATIONSHIP_FILTERS = ["mine", "friends", "following"];

function MarkerFilterControl({
  selectedMarkerFilters = [ALL_MARKER_FILTER],
  onMarkerFilterChange,
  availableSpeciesOptions = [],
  selectedSpeciesFilters = [],
  onSpeciesFilterChange,
  availableTypeOfFishingOptions = [],
  selectedTypeOfFishingFilter = "",
  onTypeOfFishingFilterChange,
  availableWayOfFishingOptions = [],
  selectedWayOfFishingFilter = "",
  onWayOfFishingFilterChange,
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [speciesQuery, setSpeciesQuery] = useState("");
  const hasAllSelected = selectedMarkerFilters.includes(ALL_MARKER_FILTER);
  const hasNoneSelected = selectedMarkerFilters.includes(NONE_MARKER_FILTER);
  const hasAllSpeciesSelected = selectedSpeciesFilters.length === 0;
  const normalizedSpeciesQuery = speciesQuery.trim().toLowerCase();
  const selectedSpeciesSet = new Set(selectedSpeciesFilters);
  const activeRelationshipFilterCount = hasAllSelected
    ? 0
    : hasNoneSelected
    ? 1
    : selectedMarkerFilters.filter((value) =>
        RELATIONSHIP_FILTERS.includes(value)
      ).length;
  const activeMetadataFilterCount =
    selectedSpeciesFilters.length +
    (selectedTypeOfFishingFilter ? 1 : 0) +
    (selectedWayOfFishingFilter ? 1 : 0);
  const activeFilterCount =
    activeRelationshipFilterCount + activeMetadataFilterCount;
  const hasActiveMetadataFilters = activeMetadataFilterCount > 0;
  const speciesLabelByValue = availableSpeciesOptions.reduce((labels, option) => {
    labels[option.value] = option.label;
    return labels;
  }, {});
  const speciesSuggestions = normalizedSpeciesQuery
    ? availableSpeciesOptions
        .filter(
          (option) =>
            !selectedSpeciesSet.has(option.value) &&
            option.label.toLowerCase().includes(normalizedSpeciesQuery)
        )
        .slice(0, 8)
    : [];
  const relationshipFilterMeta = {
    mine: {
      icon: User,
      label: t("map_filter_mine"),
    },
    friends: {
      icon: Users,
      label: t("map_filter_friends"),
    },
    following: {
      icon: UserCheck,
      label: t("map_filter_following"),
    },
  };

  useEffect(() => {
    const handleOpenMapFilters = () => {
      setIsOpen(true);
    };

    const handleCloseMapToolsPanels = () => {
      setIsOpen(false);
    };

    window.addEventListener(OPEN_MAP_FILTERS_EVENT, handleOpenMapFilters);
    window.addEventListener(
      CLOSE_MAP_TOOLS_PANELS_EVENT,
      handleCloseMapToolsPanels
    );
    return () => {
      window.removeEventListener(OPEN_MAP_FILTERS_EVENT, handleOpenMapFilters);
      window.removeEventListener(
        CLOSE_MAP_TOOLS_PANELS_EVENT,
        handleCloseMapToolsPanels
      );
    };
  }, []);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(MAP_TOOLS_PANEL_STATE_EVENT, {
        detail: { id: "filters", isOpen },
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent(MAP_TOOLS_PANEL_STATE_EVENT, {
          detail: { id: "filters", isOpen: false },
        })
      );
    };
  }, [isOpen]);

  const handleSelectAll = () => {
    onMarkerFilterChange?.([ALL_MARKER_FILTER]);
  };

  const handleSelectNone = () => {
    onMarkerFilterChange?.([NONE_MARKER_FILTER]);
  };

  const handleToggleFilter = (filterValue) => {
    if (!onMarkerFilterChange) return;

    const currentFilters =
      hasAllSelected || hasNoneSelected
        ? []
        : selectedMarkerFilters.filter(
            (value) => value !== ALL_MARKER_FILTER && value !== NONE_MARKER_FILTER
          );

    const nextFilters = currentFilters.includes(filterValue)
      ? currentFilters.filter((value) => value !== filterValue)
      : [...currentFilters, filterValue];

    onMarkerFilterChange(
      nextFilters.length > 0 ? nextFilters : [ALL_MARKER_FILTER]
    );
  };

  const handleClearSpeciesFilters = () => {
    onSpeciesFilterChange?.([]);
    setSpeciesQuery("");
  };

  const handleAddSpeciesFilter = (option) => {
    if (!onSpeciesFilterChange || selectedSpeciesSet.has(option.value)) {
      return;
    }

    onSpeciesFilterChange([...selectedSpeciesFilters, option.value]);
    setSpeciesQuery("");
  };

  const handleRemoveSpeciesFilter = (speciesValue) => {
    if (!onSpeciesFilterChange) return;

    onSpeciesFilterChange(
      selectedSpeciesFilters.filter((value) => value !== speciesValue)
    );
  };

  const handleClearMetadataFilters = () => {
    onSpeciesFilterChange?.([]);
    onTypeOfFishingFilterChange?.("");
    onWayOfFishingFilterChange?.("");
    setSpeciesQuery("");
  };

  return (
    <div className={`map-filter${isOpen ? " is-open" : ""}`}>
      <button
        type="button"
        className={`map-filter-toggle${isOpen ? " is-open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {t("map_filter_button", { defaultValue: "Filter" })}
        {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
      </button>

      {isOpen && (
        <div className="map-filter-panel">
          <span className="map-filter-panel-handle" aria-hidden="true" />

          <div className="map-filter-panel-head">
            <div className="map-filter-panel-title-wrap">
              <span className="map-filter-panel-icon" aria-hidden="true">
                <SlidersHorizontal size={18} strokeWidth={2.25} />
              </span>
              <div className="map-filter-panel-title">
                {t("map_filter_panel_title", {
                  defaultValue: "Filter catch locations",
                })}
              </div>
            </div>
            <button
              type="button"
              className="map-filter-panel-close"
              onClick={() => setIsOpen(false)}
              aria-label={t("common_close", { defaultValue: "Close" })}
            >
              <X size={17} strokeWidth={2.4} aria-hidden="true" />
            </button>
          </div>

          <div className="map-filter-section map-filter-section--species">
            <div className="map-filter-section-head">
              <div className="map-filter-section-title">
                {t("map_filter_species_title", { defaultValue: "Fish species" })}
              </div>
              {hasActiveMetadataFilters && (
                <button
                  type="button"
                  className="map-filter-clear-metadata"
                  onClick={handleClearMetadataFilters}
                >
                  {t("map_filter_clear_metadata", {
                    defaultValue: "Clear metadata",
                  })}
                </button>
              )}
            </div>

            <button
              type="button"
              className={`map-filter-all${hasAllSpeciesSelected ? " is-active" : ""}`}
              onClick={handleClearSpeciesFilters}
            >
              <span className="map-filter-all-icon" aria-hidden="true">
                <Fish size={20} strokeWidth={2.15} />
              </span>
              <span>{t("map_filter_species_all", { defaultValue: "All species" })}</span>
            </button>

            <input
              type="text"
              className="map-filter-search-input"
              value={speciesQuery}
              onChange={(event) => setSpeciesQuery(event.target.value)}
              placeholder={t("map_filter_species_search", {
                defaultValue: "Search fish species",
              })}
            />

            {selectedSpeciesFilters.length > 0 && (
              <div className="map-filter-tags">
                {selectedSpeciesFilters.map((speciesValue) => (
                  <span key={speciesValue} className="map-filter-tag">
                    <span>{speciesLabelByValue[speciesValue] || speciesValue}</span>
                    <button
                      type="button"
                      className="map-filter-tag-remove"
                      onClick={() => handleRemoveSpeciesFilter(speciesValue)}
                      aria-label={t("common_remove", { defaultValue: "Remove" })}
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
            )}

            {normalizedSpeciesQuery && (
              <div className="map-filter-suggestions">
                {speciesSuggestions.length > 0 ? (
                  speciesSuggestions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className="map-filter-suggestion"
                      onClick={() => handleAddSpeciesFilter(option)}
                    >
                      {option.label}
                    </button>
                  ))
                ) : (
                  <div className="map-filter-empty">
                    {t("map_filter_species_empty", {
                      defaultValue: "No matching species",
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="map-filter-control-row">
            <div className="map-filter-section map-filter-section--field">
              <div className="map-filter-section-title">
                <span className="map-filter-field-icon" aria-hidden="true">
                  <Waves size={15} strokeWidth={2.2} />
                </span>
                {t("map_filter_way_title", { defaultValue: "Way of fishing" })}
              </div>

              <select
                className="map-filter-select"
                value={selectedWayOfFishingFilter}
                onChange={(event) =>
                  onWayOfFishingFilterChange?.(event.target.value)
                }
              >
                <option value="">
                  {t("map_filter_way_all", { defaultValue: "All ways" })}
                </option>
                {availableWayOfFishingOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="map-filter-section map-filter-section--field">
              <div className="map-filter-section-title">
                <span className="map-filter-field-icon" aria-hidden="true">
                  <Anchor size={15} strokeWidth={2.2} />
                </span>
                {t("map_filter_type_title", { defaultValue: "Type of fishing" })}
              </div>

              <select
                className="map-filter-select"
                value={selectedTypeOfFishingFilter}
                onChange={(event) =>
                  onTypeOfFishingFilterChange?.(event.target.value)
                }
              >
                <option value="">
                  {t("map_filter_type_all", { defaultValue: "All types" })}
                </option>
                {availableTypeOfFishingOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="map-filter-section map-filter-section--sources">
            <div className="map-filter-section-title">
              {t("map_filter_sources_title")}
            </div>

            <div className="map-filter-source-segment">
              <button
                type="button"
                className={`map-filter-all map-filter-relationship-all${
                  hasAllSelected ? " is-active" : ""
                }`}
                onClick={handleSelectAll}
              >
                {t("map_filter_all", { defaultValue: "All" })}
              </button>

              <button
                type="button"
                className={`map-filter-all map-filter-none map-filter-relationship-none${
                  hasNoneSelected ? " is-active" : ""
                }`}
                onClick={handleSelectNone}
              >
                {t("map_filter_none", { defaultValue: "None" })}
              </button>
            </div>

            <div className="map-filter-options">
              {RELATIONSHIP_FILTERS.map((filterValue) => {
                const isChecked =
                  !hasAllSelected && selectedMarkerFilters.includes(filterValue);
                const FilterIcon = relationshipFilterMeta[filterValue].icon;
                return (
                  <label
                    key={filterValue}
                    className={`map-filter-option${isChecked ? " is-active" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleToggleFilter(filterValue)}
                    />
                    <span className="map-filter-option-icon" aria-hidden="true">
                      <FilterIcon size={17} strokeWidth={2.2} />
                    </span>
                    <span className="map-filter-option-label">
                      {relationshipFilterMeta[filterValue].label}
                    </span>
                    <span className="map-filter-option-check" aria-hidden="true">
                      <Check size={13} strokeWidth={2.6} />
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MapControls({
  activePage,
  selectedMarkerFilters,
  onMarkerFilterChange,
  availableSpeciesOptions,
  selectedSpeciesFilters,
  onSpeciesFilterChange,
  availableTypeOfFishingOptions,
  selectedTypeOfFishingFilter,
  onTypeOfFishingFilterChange,
  availableWayOfFishingOptions,
  selectedWayOfFishingFilter,
  onWayOfFishingFilterChange,
}) {
  if (activePage && activePage !== "map") return null;

  return (
    <div className="map-controls-stack">
      <MarkerFilterControl
        selectedMarkerFilters={selectedMarkerFilters}
        onMarkerFilterChange={onMarkerFilterChange}
        availableSpeciesOptions={availableSpeciesOptions}
        selectedSpeciesFilters={selectedSpeciesFilters}
        onSpeciesFilterChange={onSpeciesFilterChange}
        availableTypeOfFishingOptions={availableTypeOfFishingOptions}
        selectedTypeOfFishingFilter={selectedTypeOfFishingFilter}
        onTypeOfFishingFilterChange={onTypeOfFishingFilterChange}
        availableWayOfFishingOptions={availableWayOfFishingOptions}
        selectedWayOfFishingFilter={selectedWayOfFishingFilter}
        onWayOfFishingFilterChange={onWayOfFishingFilterChange}
      />
    </div>
  );
}
